/**
 * Location utility for getting GPS coordinates and handling location permissions
 */

/**
 * Request location permission and get current GPS coordinates
 * @returns {Promise<{latitude: number, longitude: number}>}
 */
export const getCurrentLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation is not supported by this browser'))
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude
        })
      },
      (error) => {
        // If user denies permission or error occurs, reject with error
        reject(error)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    )
  })
}

/**
 * Request location permission (triggers browser permission prompt)
 * @returns {Promise<boolean>} True if permission granted, false otherwise
 */
export const requestLocationPermission = async () => {
  try {
    if (!navigator.geolocation) {
      return false
    }

    // Check if permission is already granted by trying to get location
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        resolve,
        reject,
        { timeout: 1000, maximumAge: 0 }
      )
    })
    
    return true
  } catch (error) {
    // Permission denied or error
    return false
  }
}

/**
 * Check if location permission is available
 * @returns {boolean}
 */
export const hasLocationPermission = () => {
  return 'geolocation' in navigator
}

